/**
 * EscritorioDosAgentes — o escritório da Sala dos Agentes: 3D quando o navegador permite,
 * 2D quando não (ou quando a pessoa prefere).
 *
 * - WebGL indisponível, erro do renderer ou contexto perdido → cai no escritório 2D (`plano`),
 *   sem derrubar a tela.
 * - A preferência 3D/2D fica no navegador (localStorage), por pessoa.
 * - A cena só anima com movimento permitido (preferência do usuário, sem
 *   `prefers-reduced-motion`, aba visível e a sala na tela); sem isso os agentes aparecem já
 *   no lugar, sem caminhar.
 * - Tudo o que é informação fica em HTML, fora do canvas: legenda das áreas, contagem por
 *   estado e o cartão do agente selecionado (a lista completa é o roster ao lado).
 * - Clicar num personagem SELECIONA (mostra o cartão); abrir o rastro/execução é o botão do
 *   cartão — clicar não tira a pessoa da sala.
 */
import { Component, Suspense, lazy, useCallback, useMemo, useRef, useState } from 'react';
import { Box, Layers, Crosshair, X } from 'lucide-react';
import { C } from '@/theme/palette';
import { useAnimationAllowed } from '@/ui/sphere/motion';
import { webglDisponivel } from '@/ui/sphere/SphereCanvas';
import {
  COR_DO_STATUS, ROTULO_DO_STATUS, ZONAS, contagemPorStatus, corDoAgente, falaDoAgente,
  montarEscritorio,
} from './officeModel';

const Office3DScene = lazy(() => import('./Office3DScene'));

const CHAVE_DA_PREFERENCIA = 'nava.sala-dos-agentes.visao';

function lerPreferencia() {
  try {
    return window.localStorage.getItem(CHAVE_DA_PREFERENCIA) === '2d' ? '2d' : '3d';
  } catch {
    return '3d';
  }
}

function gravarPreferencia(v) {
  try {
    window.localStorage.setItem(CHAVE_DA_PREFERENCIA, v);
  } catch {
    /* sem armazenamento: a escolha vale só nesta visita */
  }
}

class ErroDoRenderer extends Component {
  constructor(props) {
    super(props);
    this.state = { erro: false };
  }

  static getDerivedStateFromError() {
    return { erro: true };
  }

  componentDidCatch(err) {
    console.warn('[sala dos agentes] cena 3D indisponível — usando a visão 2D.', err?.message || err);
    this.props.onErro?.();
  }

  render() {
    return this.state.erro ? this.props.reserva : this.props.children;
  }
}

const botao = (ativo) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', fontSize: 12,
  fontWeight: 700, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
  border: `1px solid ${ativo ? C.cyber : C.blackBorder}`,
  background: ativo ? `${C.cyber}1f` : 'rgba(8,14,32,0.72)',
  color: ativo ? C.cyber : C.neutral,
});

function Legenda({ agents, escritorio }) {
  const porStatus = contagemPorStatus(agents);
  const ordem = ['typing', 'active', 'reading', 'thinking', 'waiting', 'completed', 'failed', 'idle'];
  return (
    <div data-testid="escritorio-legenda" style={{ position: 'absolute', left: 10, bottom: 10,
      display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 10px', borderRadius: 10,
      background: 'rgba(8,14,32,0.78)', border: `1px solid ${C.blackBorder}`,
      fontSize: 11, color: C.neutral, maxWidth: 'calc(100% - 20px)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
        {Object.entries(ZONAS).map(([chave, z]) => (
          <span key={chave} title={z.descricao}>
            <strong style={{ color: C.neutral }}>{z.nome}</strong>{' '}
            <span style={{ color: C.neutralMuted }}>
              {(escritorio.porZona[chave] || []).length.toLocaleString('pt-BR')}
            </span>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px' }}>
        {ordem.filter((s) => porStatus[s]).map((s) => (
          <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%',
              background: COR_DO_STATUS[s] }} />
            {ROTULO_DO_STATUS[s]} {porStatus[s].toLocaleString('pt-BR')}
          </span>
        ))}
      </div>
    </div>
  );
}

function CartaoDoAgente({ agente, zona, detalhe, onAbrir, onFechar, rotuloDeAbrir }) {
  const cor = COR_DO_STATUS[agente.status] ?? COR_DO_STATUS.idle;
  const fala = falaDoAgente(agente, 160);
  return (
    <div role="dialog" aria-label={`Agente ${agente.name}`} data-testid="escritorio-cartao"
      style={{ position: 'absolute', right: 10, bottom: 10, width: 'min(320px, calc(100% - 20px))',
        padding: 12, borderRadius: 12, background: 'rgba(8,14,32,0.94)',
        border: `1px solid ${cor}66`, color: C.neutral, fontSize: 12.5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 4,
          background: corDoAgente(agente.agentId), flexShrink: 0 }} />
        <strong style={{ fontSize: 14, overflowWrap: 'anywhere', flex: 1 }}>{agente.name}</strong>
        <button type="button" onClick={onFechar} aria-label="Fechar o cartão do agente"
          style={{ background: 'transparent', border: 'none', color: C.neutralMuted,
            cursor: 'pointer', padding: 2, display: 'inline-flex' }}>
          <X size={15} aria-hidden="true" />
        </button>
      </div>
      <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <span style={{ color: cor, fontWeight: 700 }}>{ROTULO_DO_STATUS[agente.status] ?? agente.status}</span>
        <span style={{ color: C.neutralMuted }}>· {ZONAS[zona]?.nome}</span>
      </div>
      {fala ? <p style={{ margin: '8px 0 0', color: C.neutral, lineHeight: 1.4 }}>{fala}</p> : null}
      {detalhe ? <p style={{ margin: '6px 0 0', color: C.neutralMuted }}>{detalhe}</p> : null}
      {onAbrir ? (
        <button type="button" onClick={onAbrir} style={{ ...botao(true), marginTop: 10 }}>
          {rotuloDeAbrir}
        </button>
      ) : null}
    </div>
  );
}

/**
 * @param {{agents: object[], focusId?: string, onFocus?: (a: object) => void,
 *          plano: import('react').ReactNode, detalheDoAgente?: (a: object) => string,
 *          rotuloDeAbrir?: string}} props
 *   `plano`: o escritório 2D (reserva e alternativa). `onFocus`: abrir o rastro/execução do
 *   agente — acionado pelo botão do cartão.
 */
export function EscritorioDosAgentes({ agents = [], focusId, onFocus, plano, detalheDoAgente,
  rotuloDeAbrir = 'Abrir o rastro do agente' }) {
  const ref = useRef(null);
  const { allowed } = useAnimationAllowed(ref);
  const [gl] = useState(webglDisponivel);
  const [visao, setVisao] = useState(lerPreferencia);
  const [quebrou, setQuebrou] = useState(false);
  const [selecionadoId, setSelecionadoId] = useState(null);
  const [recentrar, setRecentrar] = useState(0);
  const escritorio = useMemo(() => montarEscritorio(agents), [agents]);

  const escolher = useCallback((v) => {
    setVisao(v);
    gravarPreferencia(v);
  }, []);
  const aoCriar = useCallback((estado) => {
    const el = estado?.gl?.domElement;
    el?.addEventListener?.('webglcontextlost', (e) => { e.preventDefault(); setQuebrou(true); },
      { once: true });
  }, []);

  const pode3d = gl && !quebrou;
  const em3d = pode3d && visao === '3d';
  const selecionado = agents.find((a) => a.agentId === selecionadoId) || null;

  if (!em3d) {
    return (
      <div ref={ref} data-testid="escritorio" data-visao="2d"
        style={{ position: 'relative', flex: 1, minWidth: 420, display: 'flex' }}>
        {plano}
        {pode3d ? (
          <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 6 }}>
            <button type="button" style={botao(false)} onClick={() => escolher('3d')}
              aria-label="Ver o escritório em 3D">
              <Box size={13} aria-hidden="true" /> Ver em 3D
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  const carregando = (
    <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
      color: C.neutralMuted, fontSize: 13 }}>Montando o escritório…</div>
  );
  return (
    <div ref={ref} data-testid="escritorio" data-visao="3d"
      data-animando={allowed ? 'sim' : 'nao'}
      style={{ position: 'relative', flex: 1, minWidth: 420, height: 'clamp(470px, 62vh, 760px)',
        borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.blackBorder}`,
        background: '#070d1f' }}>
      <ErroDoRenderer reserva={plano} onErro={() => setQuebrou(true)}>
        <Suspense fallback={carregando}>
          <Office3DScene escritorio={escritorio} focusId={selecionadoId ?? focusId}
            onFocus={(a) => setSelecionadoId(a?.agentId ?? null)} animar={allowed}
            recentrar={recentrar} onCreated={aoCriar} />
        </Suspense>
      </ErroDoRenderer>

      <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6 }}>
        <button type="button" style={botao(false)} onClick={() => setRecentrar((n) => n + 1)}
          aria-label="Recentralizar a câmera">
          <Crosshair size={13} aria-hidden="true" /> Recentralizar
        </button>
        <button type="button" style={botao(false)} onClick={() => escolher('2d')}
          aria-label="Ver o escritório em 2D">
          <Layers size={13} aria-hidden="true" /> Ver em 2D
        </button>
      </div>
      <p style={{ position: 'absolute', top: 12, left: 12, margin: 0, fontSize: 11,
        color: C.neutralMuted, pointerEvents: 'none' }}>
        Arraste para girar · role para aproximar · clique num agente
      </p>

      <Legenda agents={agents} escritorio={escritorio} />
      {selecionado ? (
        <CartaoDoAgente
          agente={selecionado}
          zona={escritorio.lugares[selecionado.agentId]?.zona}
          detalhe={detalheDoAgente?.(selecionado)}
          rotuloDeAbrir={rotuloDeAbrir}
          onAbrir={onFocus ? () => onFocus(selecionado) : null}
          onFechar={() => setSelecionadoId(null)}
        />
      ) : null}
    </div>
  );
}
